import { getFormErrors } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { Tabs } from "@msuite/ui/tabs";
import type { WorkspaceSchema } from "@msuite/utils/schemas";
import { useFormContext } from "react-hook-form";
import { WorkspaceFormBankAccount } from "./workspace-form-bank-account";
import { WorkspaceFormBusinessHours } from "./workspace-form-business-hours";
import { WorkspaceFormDetails } from "./workspace-form-details";
import { WorkspaceFormGeneral } from "./workspace-form-general";
import { WorkspaceFormImages, type WorkspaceFormImagesProps } from "./workspace-form-images";
import { WorkspaceFormLegalInformation } from "./workspace-form-legal-information";

/** Props Interface */
interface WorkspaceFormProps extends WorkspaceFormImagesProps {}

export function WorkspaceForm({
	workspaceLogoFileController,
	workspaceLetterpaperFileController,
	workspaceId,
}: WorkspaceFormProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useFormContext<WorkspaceSchema>();

	/** Render */
	return (
		<Tabs
			panelKey="workspace-form-tabs"
			groups={[
				{
					name: "General",
					panels: [
						{
							label: t("general.general"),
							panel: () => <WorkspaceFormGeneral />,
							errors: getFormErrors(methods.formState.errors, ["name", "color"]),
							cyId: "workspace-form-general-tab",
						},
						{
							label: t("general.details"),
							panel: () => <WorkspaceFormDetails />,
							cyId: "workspace-form-details-tab",
							errors: getFormErrors(methods.formState.errors, [
								"companyName",
								"streetName",
								"houseNumber",
								"zipCode",
								"city",
								"region",
								"phone",
								"email",
								"web",
							]),
						},
						{
							label: t("organization.business_hours"),
							panel: () => <WorkspaceFormBusinessHours />,
							cyId: "workspace-form-business-hours-tab",
							errors: getFormErrors(methods.formState.errors, ["businessHours"]),
						},
						{
							label: t("organization.bank_account"),
							panel: () => <WorkspaceFormBankAccount />,
							cyId: "workspace-form-bank-account-tab",
							errors: getFormErrors(methods.formState.errors, ["bankAccountHolder", "iban", "bic"]),
						},
						{
							label: t("images.images"),
							panel: () => (
								<WorkspaceFormImages
									workspaceLogoFileController={workspaceLogoFileController}
									workspaceLetterpaperFileController={workspaceLetterpaperFileController}
									workspaceId={workspaceId}
								/>
							),
							errors: null,
							cyId: "workspace-form-images-tab",
						},
						{
							label: t("legal.legal_information"),
							panel: () => <WorkspaceFormLegalInformation />,
							errors: getFormErrors(methods.formState.errors, ["legal"]),
							cyId: "workspace-form-legal-information-tab",
						},
					],
				},
			]}
		/>
	);
}
